/**
 * upload/draftStore.ts — local draft persistence for the Upload a Tool flow.
 *
 * WHY localStorage: this repository has no client-side persistence pattern yet (nothing in
 * packages/web touches localStorage or sessionStorage), and the one server-side draft table that
 * exists — `app.submission_draft` (0005) — has NO reader and NO writer anywhere in the codebase.
 * Building a backend draft API is a larger slice than this task, and Will's flow only needs the
 * draft to survive a refresh, a closed overlay and a reopened tab on the same machine. So: local,
 * per-browser. `app.submission_draft` remains the natural home if drafts are ever needed across
 * devices, and the shape stored here maps onto its `payload JSONB` column when that day comes.
 *
 * SCOPING — THE THING THAT MUST NOT GO WRONG: a draft is keyed by BOTH the signed-in user and the
 * selected automation. Two different flows must never share answers (their components are different,
 * so restoring across them would attach explanations to the wrong steps), and two users sharing a
 * machine must never see each other's in-progress text. Hence `<user_id>:<workflowId>` in the key.
 *
 * STALENESS: the draft records the `definitionFingerprint` of the component list it was written
 * against. If the builder edits their flow in Power Automate mid-draft, the fingerprint changes and
 * the caller reconciles (shared `reconcileExplanations`) rather than blindly re-attaching answers.
 *
 * EVERY READ IS DEFENSIVE. localStorage can be unavailable (private mode, disabled cookies, quota),
 * and its contents are user-editable. A malformed or foreign-shaped entry is treated as "no draft"
 * rather than throwing — losing a draft is bad, but crashing the upload page is worse.
 */
import type { ExplanationMap } from '@trailblazer/shared';

/**
 * Bump ONLY on a breaking change to UploadDraft's shape. A version mismatch makes old drafts
 * unreadable by design — silently importing a stale shape into a new wizard is how "resume" starts
 * restoring nonsense.
 */
const DRAFT_VERSION = 1;

/** Namespaced so it is obvious in devtools which app owns the key, and cheap to enumerate. */
const KEY_PREFIX = `trailblazer:upload-draft:v${DRAFT_VERSION}`;

/** Which wizard stage the builder had reached. Restored so Resume returns them where they left off. */
export type UploadStage = 'details' | 'instructions' | 'review';

/** The Details-stage answers. Mirrors the fields POST /api/tools requires, as strings from inputs. */
export interface DraftDetails {
  name: string;
  description: string;
  problemSolved: string;
  categoryId: number;
  accessId: number;
  departmentId: number;
  estimatedTimeSaved: string;
  dependencies: string;
  requirements: string;
  tags: string[];
}

/** Everything persisted for one in-progress upload of one automation. */
export interface UploadDraft {
  version: number;
  /** The automation this draft belongs to. Part of the key too; stored for validation on read. */
  workflowId: string;
  /** The flow's name at the time the draft was made — shown on the Resume prompt for recognition. */
  sourceName: string;
  /** Component-structure digest this draft's explanations were written against (staleness check). */
  definitionFingerprint: string;
  stage: UploadStage;
  details: DraftDetails;
  /** Explanations keyed by component id, so answers cannot shift between steps. */
  explanations: ExplanationMap;
  /** The data-safety attestation (UP-F23) tick, so Resume does not silently re-tick it. */
  attested: boolean;
  /**
   * The self-vouch tick: ticked ⇒ "does not need AI SWAT verification", publishes as a Draft;
   * unticked ⇒ held for the AI SWAT team (Pending). Persisted so Resume does not silently re-tick it,
   * and defaults to false — the safe route (a resumed draft errs towards being verified, not skipped).
   */
  takesResponsibility: boolean;
  /** The builder's answer to the duplicate prompt, so Resume does not re-interrogate them. */
  duplicateAcknowledged: boolean;
  /** ISO timestamps — `updatedAt` is shown on the Resume prompt ("last saved …"). */
  createdAt: string;
  updatedAt: string;
}

/** The draft key for one user + one automation. Both parts are required; neither may be blank. */
function draftKey(userId: string, workflowId: string): string {
  return `${KEY_PREFIX}:${userId}:${workflowId}`;
}

/**
 * localStorage, or null when it cannot be used.
 *
 * Access is wrapped because merely TOUCHING window.localStorage throws in some privacy modes — so
 * this cannot be a simple truthiness check. Everything downstream treats null as "drafts are not
 * available here", which degrades the wizard to a working-but-unsaved experience rather than a
 * broken page.
 */
function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    // A probe write: quota-exceeded and security errors only surface on use, not on access.
    const probe = `${KEY_PREFIX}:__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

/** True when drafts can be persisted at all — the wizard tells the builder when they cannot. */
export function draftsAvailable(): boolean {
  return storage() !== null;
}

/**
 * Read the draft for this user + automation, or null when there is none, it is unreadable, or it
 * belongs to a different automation than its key claims (a tampered or hand-edited entry).
 */
export function loadDraft(userId: string, workflowId: string): UploadDraft | null {
  const store = storage();
  if (!store || !userId || !workflowId) return null;

  let raw: string | null;
  try {
    raw = store.getItem(draftKey(userId, workflowId));
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    const draft = narrowDraft(parsed);
    // Belt and braces: the key already scopes by workflow, but a draft whose body names a DIFFERENT
    // automation is corrupt and must never be restored — that is precisely the cross-contamination
    // this module exists to prevent.
    if (!draft || draft.workflowId !== workflowId) return null;
    return draft;
  } catch {
    return null;
  }
}

/**
 * Write the draft. Silently no-ops when storage is unavailable or full — autosave must never
 * interrupt typing with an error, and the wizard already tells the builder up front when drafts
 * cannot be saved on this browser.
 */
export function saveDraft(userId: string, draft: UploadDraft): void {
  const store = storage();
  if (!store || !userId || !draft.workflowId) return;
  try {
    store.setItem(draftKey(userId, draft.workflowId), JSON.stringify(draft));
  } catch {
    // Quota exceeded or a mid-session permission change. Nothing useful to do here.
  }
}

/**
 * Remove the draft for one automation. Called on a successful upload and on an explicit "start new"
 * / discard — never merely because the overlay closed or the page reloaded.
 */
export function clearDraft(userId: string, workflowId: string): void {
  const store = storage();
  if (!store || !userId || !workflowId) return;
  try {
    store.removeItem(draftKey(userId, workflowId));
  } catch {
    // Ignore: a draft we cannot delete is a leftover, not a failure worth surfacing.
  }
}

/**
 * Every stored draft key for one user, newest write NOT yet ordered. Enumerates localStorage rather
 * than tracking an index, because the index would be one more thing to keep in sync (and to get
 * wrong). The key layout is `<KEY_PREFIX>:<userId>:<workflowId>`, so we match on the prefix + user
 * and read the workflowId off the tail. Fully defensive: a null store or a throw mid-enumeration
 * yields an empty list, the same "no drafts here" the readers already handle.
 */
function draftKeysForUser(store: Storage, userId: string): string[] {
  const prefix = `${KEY_PREFIX}:${userId}:`;
  const keys: string[] = [];
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(prefix)) keys.push(key);
    }
  } catch {
    return [];
  }
  return keys;
}

/**
 * The user's ONE current draft: the most recently updated in-progress upload, or null when there is
 * none. This is the single-draft model — the surface offers "continue where you left off" for the
 * LATEST unfinished upload, whatever automation it was for, rather than one resume per flow. Ties on
 * `updatedAt` (same-millisecond writes) are broken arbitrarily but stably by string order; in
 * practice there is only ever one draft because `clearOtherDrafts` evicts the rest when a new upload
 * begins. Defensive throughout: unreadable/foreign entries are skipped, never thrown on.
 */
export function latestDraft(userId: string): UploadDraft | null {
  const store = storage();
  if (!store || !userId) return null;
  let best: UploadDraft | null = null;
  for (const key of draftKeysForUser(store, userId)) {
    // The workflowId is the segment after `<KEY_PREFIX>:<userId>:` — reuse loadDraft so the same
    // narrowing + identity check applies (a tampered entry naming a different flow is rejected there).
    const workflowId = key.slice(`${KEY_PREFIX}:${userId}:`.length);
    const draft = loadDraft(userId, workflowId);
    if (!draft) continue;
    if (!best || draft.updatedAt > best.updatedAt) best = draft;
  }
  return best;
}

/**
 * Enforce the single-draft model: remove every draft for this user EXCEPT the one for
 * `keepWorkflowId`. Called when a genuinely new upload begins (a fresh draft is created), so
 * starting over replaces whatever unfinished upload was there before. Resuming a draft must NOT call
 * this — that is continuing the one draft, not starting a new one.
 */
export function clearOtherDrafts(userId: string, keepWorkflowId: string): void {
  const store = storage();
  if (!store || !userId) return;
  const keep = draftKey(userId, keepWorkflowId);
  for (const key of draftKeysForUser(store, userId)) {
    if (key === keep) continue;
    try {
      store.removeItem(key);
    } catch {
      // Ignore: a leftover we cannot delete is not a failure worth surfacing.
    }
  }
}

/** Blank Details, given the reference-data defaults the form starts from. */
export function emptyDetails(defaults: {
  categoryId: number;
  accessId: number;
  departmentId: number;
}): DraftDetails {
  return {
    name: '',
    description: '',
    problemSolved: '',
    categoryId: defaults.categoryId,
    accessId: defaults.accessId,
    departmentId: defaults.departmentId,
    estimatedTimeSaved: '10',
    dependencies: '',
    requirements: '',
    tags: [],
  };
}

/** A fresh draft for one automation. `createdAt`/`updatedAt` start equal. */
export function newDraft(args: {
  workflowId: string;
  sourceName: string;
  definitionFingerprint: string;
  details: DraftDetails;
  nowIso: string;
}): UploadDraft {
  return {
    version: DRAFT_VERSION,
    workflowId: args.workflowId,
    sourceName: args.sourceName,
    definitionFingerprint: args.definitionFingerprint,
    stage: 'details',
    details: args.details,
    explanations: {},
    attested: false,
    takesResponsibility: false,
    duplicateAcknowledged: false,
    createdAt: args.nowIso,
    updatedAt: args.nowIso,
  };
}

// ── Defensive narrowing ──────────────────────────────────────────────────────────────────────────
//
// Everything below rebuilds the draft field by field from an untyped parse. localStorage content is
// user-editable, so nothing is trusted wholesale: a missing or wrong-typed field falls back to a
// safe default, and a draft missing its identity is rejected outright.

const STAGES: UploadStage[] = ['details', 'instructions', 'review'];

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** Narrow the explanation map: string keys to string values, anything else dropped. */
function narrowExplanations(v: unknown): ExplanationMap {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: ExplanationMap = {};
  for (const [key, value] of Object.entries(v as Record<string, unknown>)) {
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

function narrowDetails(v: unknown): DraftDetails {
  const d = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
  return {
    name: str(d.name),
    description: str(d.description),
    problemSolved: str(d.problemSolved),
    categoryId: num(d.categoryId, 0),
    accessId: num(d.accessId, 0),
    departmentId: num(d.departmentId, 0),
    estimatedTimeSaved: str(d.estimatedTimeSaved, '10'),
    dependencies: str(d.dependencies),
    requirements: str(d.requirements),
    tags: Array.isArray(d.tags) ? d.tags.filter((t): t is string => typeof t === 'string') : [],
  };
}

function narrowDraft(v: unknown): UploadDraft | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const d = v as Record<string, unknown>;

  // A draft from an older/newer shape is not migrated — see DRAFT_VERSION.
  if (num(d.version, -1) !== DRAFT_VERSION) return null;
  // Without the automation it belongs to, a draft cannot be safely restored to anything.
  if (typeof d.workflowId !== 'string' || d.workflowId === '') return null;

  const stage = STAGES.includes(d.stage as UploadStage) ? (d.stage as UploadStage) : 'details';
  const createdAt = str(d.createdAt);
  return {
    version: DRAFT_VERSION,
    workflowId: d.workflowId,
    sourceName: str(d.sourceName),
    definitionFingerprint: str(d.definitionFingerprint),
    stage,
    details: narrowDetails(d.details),
    explanations: narrowExplanations(d.explanations),
    attested: d.attested === true,
    takesResponsibility: d.takesResponsibility === true,
    duplicateAcknowledged: d.duplicateAcknowledged === true,
    createdAt,
    updatedAt: str(d.updatedAt, createdAt),
  };
}
